/* Shadow Box Builder — felt recolor engine (self-installing)
 * Hooyah Woodworks.
 *
 * WHAT IT DOES
 * Recolors the felt inside the case PHOTO to the customer's chosen felt color, instead of
 * painting a flat rectangle over the photo. Frame, wall, flag and nameplate are left
 * untouched, so nothing depends on the block's interior x/y/w/h sliders being calibrated.
 *
 * HOW IT ATTACHES
 * Self-installing — shadow-box-builder.js needs no edit. The builder already writes the
 * chosen felt onto #sbb-interior as a background color whenever a felt is picked. This
 * watches that element, reads the color the builder just set, suppresses the flat overlay,
 * and recolors #sbb-case-img instead.
 *
 * HOW THE RECOLOR WORKS
 * Reference color = median of 5 samples at the photo's center (bare felt on an empty case).
 *
 * The recolored area is then grown by FLOOD FILL from those same center samples, stepping
 * only through neighboring pixels that are themselves within TOL of the reference. Pixels
 * are recolored only if the fill reaches them.
 *
 * WHY THE FILL, AND DO NOT REMOVE IT
 * The previous version scanned every pixel in the image and recolored anything within TOL
 * of the reference, with no test for where that pixel was. A folded flag's navy canton is
 * within 40 RGB of dark navy felt, so the canton recolored along with the felt — maroon
 * bleeding across the stars. That is not a TOL tuning problem: no threshold separates navy
 * felt from navy canton, because they are the same color. Lowering TOL far enough to spare
 * the canton also abandons the shaded corners of the felt.
 *
 * The fill fixes it geometrically rather than chromatically. The wood divider that carries
 * the flag runs corner to corner and physically seals the felt region off from the flag
 * region, so a fill seeded in the felt cannot reach the canton no matter how similar the
 * two colors are. This needs no per-image coordinates and holds for any case photo where
 * the flag sits behind its own divider.
 *
 * FAILS SAFE
 * Any error (CORS taint, decode failure) leaves the original photo on screen untouched.
 * An empty fill also resolves null, leaving the photo untouched rather than half-painted.
 *
 * TUNING
 * TOL is still the one knob, and still governs how much of the felt the fill can reach.
 * Raise it if patches of felt are missed; lower it if the fill leaks into the wood frame.
 * Default 40 (RGB euclidean distance).
 */
(function () {
  'use strict';

  var TOL = 40;
  var FEATHER = TOL * 0.72;
  var MAX = 1400;
  var cache = new Map();

  /* Seed points, as fractions of the photo. These are the same samples used to derive the
     reference color — the center of the photo is bare felt on an empty case. */
  var SEEDS = [[0.5, 0.5], [0.45, 0.45], [0.55, 0.45], [0.45, 0.55], [0.55, 0.55]];

  function hexToRgb(hex) {
    var m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
    if (!m) return null;
    var n = parseInt(m[1], 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }

  /* Pull the felt color out of whatever the builder wrote to the interior background.
     Browsers normalize inline colors to rgb()/rgba(); hex may also survive. */
  function parseColor(bg) {
    if (!bg || bg === 'transparent' || bg === 'none') return null;
    var rgb = bg.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?\s*\)/g);
    if (rgb && rgb.length) {
      /* The builder appends the felt color last, after any gradient stops. */
      var last = rgb[rgb.length - 1];
      var m = last.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?\s*\)/);
      if (m) {
        if (m[4] !== undefined && parseFloat(m[4]) === 0) return null;
        return [+m[1], +m[2], +m[3]];
      }
    }
    var hex = bg.match(/#[0-9a-f]{6}/i);
    if (hex) return hexToRgb(hex[0]);
    return null;
  }

  function recolor(src, rgb) {
    var key = src + '|' + rgb.join(',');
    if (cache.has(key)) return cache.get(key);

    var p = new Promise(function (resolve) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () {
        try {
          var w = img.naturalWidth, h = img.naturalHeight;
          var sc = Math.min(1, MAX / Math.max(w, h));
          w = Math.max(1, Math.round(w * sc));
          h = Math.max(1, Math.round(h * sc));

          var c = document.createElement('canvas');
          c.width = w; c.height = h;
          var ctx = c.getContext('2d');
          ctx.drawImage(img, 0, 0, w, h);

          var id = ctx.getImageData(0, 0, w, h), d = id.data;

          var samp = SEEDS.map(function (pt) {
            var i = ((Math.round(h * pt[1]) * w) + Math.round(w * pt[0])) * 4;
            return [d[i], d[i + 1], d[i + 2]];
          });
          samp.sort(function (a, b) { return (a[0] + a[1] + a[2]) - (b[0] + b[1] + b[2]); });
          var ref = samp[2];
          var refLuma = Math.max(24, ref[0] * 0.299 + ref[1] * 0.587 + ref[2] * 0.114);

          /* Squared distance keeps the fill loop free of Math.sqrt — it runs once per
             neighbor test across the whole region, so it is the hot path. */
          var TOL2 = TOL * TOL;
          function dist2(idx) {
            var i = idx * 4;
            var dr = d[i] - ref[0], dg = d[i + 1] - ref[1], db = d[i + 2] - ref[2];
            return dr * dr + dg * dg + db * db;
          }

          var n = w * h;
          var seen = new Uint8Array(n);
          var stack = [];
          var filled = 0;

          for (var s = 0; s < SEEDS.length; s++) {
            var si = (Math.round(h * SEEDS[s][1]) * w) + Math.round(w * SEEDS[s][0]);
            if (si >= 0 && si < n && !seen[si] && dist2(si) <= TOL2) {
              seen[si] = 1;
              stack.push(si);
            }
          }

          while (stack.length) {
            var idx = stack.pop();
            filled++;
            var x = idx % w, y = (idx / w) | 0;
            var a;
            if (x > 0)     { a = idx - 1; if (!seen[a] && dist2(a) <= TOL2) { seen[a] = 1; stack.push(a); } }
            if (x < w - 1) { a = idx + 1; if (!seen[a] && dist2(a) <= TOL2) { seen[a] = 1; stack.push(a); } }
            if (y > 0)     { a = idx - w; if (!seen[a] && dist2(a) <= TOL2) { seen[a] = 1; stack.push(a); } }
            if (y < h - 1) { a = idx + w; if (!seen[a] && dist2(a) <= TOL2) { seen[a] = 1; stack.push(a); } }
          }

          /* Nothing reachable — the center samples were not felt. Leave the photo alone
             rather than shipping a partial repaint. */
          if (!filled) { resolve(null); return; }

          for (var j = 0; j < n; j++) {
            if (!seen[j]) continue;
            var i2 = j * 4;
            var dist = Math.sqrt(dist2(j));
            var luma = (d[i2] * 0.299 + d[i2 + 1] * 0.587 + d[i2 + 2] * 0.114) / refLuma;
            var soft = dist > FEATHER ? (TOL - dist) / (TOL - FEATHER) : 1;
            d[i2]     += (Math.min(255, rgb[0] * luma) - d[i2])     * soft;
            d[i2 + 1] += (Math.min(255, rgb[1] * luma) - d[i2 + 1]) * soft;
            d[i2 + 2] += (Math.min(255, rgb[2] * luma) - d[i2 + 2]) * soft;
          }

          ctx.putImageData(id, 0, 0);
          resolve(c.toDataURL('image/jpeg', 0.92));
        } catch (e) {
          resolve(null);
        }
      };
      img.onerror = function () { resolve(null); };
      img.src = src;
    });

    cache.set(key, p);
    return p;
  }

  function install() {
    var interior = document.getElementById('sbb-interior');
    var caseImg = document.getElementById('sbb-case-img');
    if (!interior || !caseImg) return false;

    var busy = false;
    var pending = null;

    function baseSrc() {
      /* Remember the real photo URL so we never recolor an already-recolored data URL. */
      var s = caseImg.getAttribute('src') || '';
      if (s.indexOf('data:') !== 0) caseImg.dataset.sbbBase = s;
      return caseImg.dataset.sbbBase || '';
    }

    function sync() {
      var rgb = parseColor(interior.style.background || interior.style.backgroundColor);
      var src = baseSrc();

      /* Suppress the builder's flat overlay either way — the photo carries the color now. */
      if (interior.style.background !== 'transparent') {
        interior.style.background = 'transparent';
        interior.style.boxShadow = 'none';
      }

      if (!src) return;

      if (!rgb) {
        /* No felt chosen — show the untouched photo. */
        if (caseImg.getAttribute('src') !== src) caseImg.setAttribute('src', src);
        return;
      }

      var token = src + '|' + rgb.join(',');
      if (caseImg.dataset.sbbApplied === token) return;

      if (busy) { pending = token; return; }
      busy = true;

      recolor(src, rgb).then(function (url) {
        busy = false;
        if (url) {
          caseImg.dataset.sbbApplied = token;
          caseImg.setAttribute('src', url);
        }
        if (pending && pending !== token) { pending = null; sync(); }
        else { pending = null; }
      });
    }

    new MutationObserver(sync).observe(interior, { attributes: true, attributeFilter: ['style'] });
    new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var v = caseImg.getAttribute('src') || '';
        if (v.indexOf('data:') !== 0) { caseImg.dataset.sbbApplied = ''; sync(); return; }
      }
    }).observe(caseImg, { attributes: true, attributeFilter: ['src'] });

    sync();
    return true;
  }

  function boot() {
    if (install()) return;
    /* Builder markup may render after this script — retry briefly, then give up quietly. */
    var tries = 0;
    var iv = setInterval(function () {
      if (install() || ++tries > 40) clearInterval(iv);
    }, 150);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
